import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CreateItemModal from '../knowledge/CreateItemModal';
import CreateCollectionModal from '../knowledge/CreateCollectionModal';
import CreateProductModal from '../knowledge/CreateProductModal';
import UploadDocumentModal from '../knowledge/UploadDocumentModal';

/**
 * FloatingActionMenu - Expandable FAB (Floating Action Button) with gradient styling
 *
 * A reusable component that displays a main + button in the bottom-right corner.
 * When clicked, it expands to show multiple action buttons arranged vertically.
 *
 * USAGE EXAMPLES:
 *
 * 1. Knowledge variant (default) - Used in Knowledge.jsx and Home.jsx:
 *    Shows actions for creating knowledge items: Product, Document, Collection, Item
 *
 *    <FloatingActionMenu
 *      collections={collections}
 *      products={products}
 *      onRefreshProducts={fetchProducts}
 *      onRefreshCollections={fetchCollections}
 *    />
 *
 * 2. Chat variant - Shows navigation actions (currently unused):
 *    <FloatingActionMenu variant="chat" />
 *
 * STYLING:
 * - Main button and all child buttons use gradient: bg-gradient-to-r from-purple-600 to-blue-600
 * - Hover states: from-purple-700 to-blue-700
 * - Child buttons appear above the main button with staggered animation
 *
 * @param {Object} props
 * @param {string} props.variant - 'knowledge' (default) or 'chat'
 * @param {Array} props.collections - Array of collection objects (required for knowledge variant)
 * @param {Array} props.products - Array of product objects (required for knowledge variant)
 * @param {Function} props.onRefreshProducts - Callback to refresh products list after creation
 * @param {Function} props.onRefreshCollections - Callback to refresh collections list after creation
 */
export default function FloatingActionMenu({ variant = 'knowledge', collections = [], products = [], onRefreshProducts, onRefreshCollections }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [productModalContext, setProductModalContext] = useState(null);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Add listener with a small delay to avoid immediate triggering
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCreateItemClick = () => {
    // Check if products exist before opening Create Item modal
    if (!products || products.length === 0) {
      // No products exist, open Create Product modal instead
      setProductModalContext('createItem');
      setShowCreateProductModal(true);
    } else {
      // Products exist, open Create Item modal
      setShowCreateItemModal(true);
    }
  };

  const handleCreateCollectionClick = () => {
    // Check if products exist before opening Create Collection modal
    if (!products || products.length === 0) {
      // No products exist, open Create Product modal instead
      setProductModalContext('createCollection');
      setShowCreateProductModal(true);
    } else {
      // Products exist, open Create Collection modal
      setShowCreateCollectionModal(true);
    }
  };

  const handleUploadDocumentClick = () => {
    // Check if products exist before opening Upload Document modal
    if (!products || products.length === 0) {
      // No products exist, open Create Product modal instead
      setProductModalContext('uploadDocument');
      setShowCreateProductModal(true);
    } else {
      // Products exist, open Upload Document modal
      setShowUploadDocumentModal(true);
    }
  };

  const handleProductCreated = async () => {
    // Refresh products list
    if (onRefreshProducts) {
      await onRefreshProducts();
    }

    // Open the appropriate modal based on what triggered the product creation
    if (productModalContext === 'createItem') {
      setShowCreateItemModal(true);
    } else if (productModalContext === 'createCollection') {
      setShowCreateCollectionModal(true);
    } else if (productModalContext === 'uploadDocument') {
      setShowUploadDocumentModal(true);
    }

    // Clear the context
    setProductModalContext(null);
  };

  const knowledgeActions = [
    {
      id: 'item',
      label: 'Create Item',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: handleCreateItemClick,
    },
    {
      id: 'collection',
      label: 'Create Collection',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      onClick: handleCreateCollectionClick,
    },
    {
      id: 'upload',
      label: 'Upload Document',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      onClick: handleUploadDocumentClick,
    },
    {
      id: 'product',
      label: 'Create Product',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      onClick: () => {
        setProductModalContext(null); // Direct click, no context message
        setShowCreateProductModal(true);
      },
    },
  ].reverse();

  const chatActions = [
    {
      id: 'new-chat',
      label: 'New Chat',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      onClick: () => navigate('/home'),
    },
    {
      id: 'knowledge',
      label: 'Knowledge Library',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      onClick: () => navigate('/knowledge'),
    },
    {
      id: 'forum',
      label: 'Forum',
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
        </svg>
      ),
      onClick: () => navigate('/questions'),
    },
  ];

  const actions = variant === 'chat' ? chatActions : knowledgeActions;

  // Navigation buttons - appear to left of main FAB, dynamically shown based on current page
  const allNavButtons = [
    {
      id: 'knowledge',
      path: '/knowledge',
      label: 'Knowledge',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    },
    {
      id: 'questions',
      path: '/questions',
      label: 'Questions',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
    {
      id: 'home',
      path: '/home',
      label: 'Home',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
  ];

  // Determine which buttons to show based on current page
  let navigationButtons = [];
  if (location.pathname === '/knowledge') {
    // On Knowledge page: show Questions (upper) and Home (lower)
    navigationButtons = [
      allNavButtons.find(btn => btn.id === 'questions'),
      allNavButtons.find(btn => btn.id === 'home'),
    ].filter(Boolean);
  } else if (location.pathname === '/questions') {
    // On Questions page: show Knowledge (upper) and Home (lower)
    navigationButtons = [
      allNavButtons.find(btn => btn.id === 'knowledge'),
      allNavButtons.find(btn => btn.id === 'home'),
    ].filter(Boolean);
  } else if (location.pathname === '/home') {
    // On Home page: show Knowledge (upper) and Questions (lower)
    navigationButtons = [
      allNavButtons.find(btn => btn.id === 'knowledge'),
      allNavButtons.find(btn => btn.id === 'questions'),
    ].filter(Boolean);
  } else {
    // On other pages: show all buttons except current page
    navigationButtons = allNavButtons.filter(btn => location.pathname !== btn.path).slice(0, 2);
  }

  return (
    <>
      <div ref={menuRef} className="fixed bottom-8 right-8 z-40">
        {/* Child Action Buttons - Absolutely positioned above the main button */}
        {isOpen && (
          <div className="absolute bottom-24 right-0 flex flex-col items-end space-y-3" style={{ zIndex: 20 }}>
            {actions.map((action, index) => (
              <div
                key={action.id}
                className="group relative"
                style={{
                  animation: `fabSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 40}ms both`,
                }}
              >
                {/* Tooltip */}
                <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap scale-95 group-hover:scale-100">
                  <div className="bg-gray-900/95 backdrop-blur-sm text-sm font-medium px-3 py-2 rounded-lg shadow-xl border border-gray-700/50" style={{ color: 'var(--color-text-on-accent)' }}>
                    {action.label}
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => {
                    action.onClick();
                    setIsOpen(false);
                  }}
                  className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-2xl transition-all duration-150 flex items-center justify-center hover:scale-110 active:scale-95"
                  style={{ color: 'var(--color-text-on-accent)' }}
                >
                  {action.icon}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Navigation Sub-Buttons - Positioned to the left, stacked vertically with offset */}
        {isOpen && navigationButtons.map((navBtn, index) => (
          <div
            key={navBtn.id}
            className="group absolute"
            style={{
              right: index === 0 ? '4.5rem' : '6.25rem', // Upper button (index 0) more to the right
              bottom: index === 0 ? '4.75rem' : '0.5rem', // Upper and lower positioning
              animation: `navOrbit 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 80}ms both`,
            }}
          >
            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none whitespace-nowrap scale-95 group-hover:scale-100">
              <div className="bg-gray-900/95 backdrop-blur-sm text-sm font-medium px-3 py-2 rounded-lg shadow-xl border border-gray-700/50" style={{ color: 'var(--color-text-on-accent)' }}>
                {navBtn.label}
              </div>
            </div>

            {/* Navigation Button */}
            <button
              onClick={() => navigate(navBtn.path)}
              className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-2xl transition-all duration-200 flex items-center justify-center hover:scale-110 active:scale-95"
              style={{ color: 'var(--color-text-on-accent)' }}
              title={navBtn.label}
            >
              {navBtn.icon}
            </button>
          </div>
        ))}

        {/* Main FAB Button - Fixed position */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-20 h-20 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center justify-center hover:scale-105 active:scale-95 opacity-90 hover:opacity-100 ${isOpen ? 'rotate-45' : ''}`}
          style={{ color: 'var(--color-text-on-accent)' }}
        >
          <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes fabSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes navOrbit {
          from {
            opacity: 0;
            transform: translateX(30px) scale(0.5);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
      `}</style>

      {/* Knowledge variant modals - only render for knowledge variant */}
      {variant === 'knowledge' && (
        <>
          {/* Create Item Modal - Rendered outside to avoid z-index stacking issues */}
          <CreateItemModal
            isOpen={showCreateItemModal}
            onClose={() => setShowCreateItemModal(false)}
            collections={collections}
            onItemCreated={onRefreshCollections}
          />

          {/* Create Collection Modal - Rendered outside to avoid z-index stacking issues */}
          <CreateCollectionModal
            isOpen={showCreateCollectionModal}
            onClose={() => setShowCreateCollectionModal(false)}
            items={[]}
          />

          {/* Create Product Modal - Rendered outside to avoid z-index stacking issues */}
          <CreateProductModal
            isOpen={showCreateProductModal}
            onClose={() => {
              setShowCreateProductModal(false);
              setProductModalContext(null);
            }}
            onProductCreated={handleProductCreated}
            triggeredFrom={productModalContext}
          />

          {/* Upload Document Modal - Rendered outside to avoid z-index stacking issues */}
          <UploadDocumentModal
            isOpen={showUploadDocumentModal}
            onClose={() => setShowUploadDocumentModal(false)}
            onDocumentImported={onRefreshCollections}
          />
        </>
      )}
    </>
  );
}
